import ReactDOM from "react-dom/client";
import React from "react";
import { globalState } from "../framework/globalState.ts";
import ErrorBoundary from "../react/ErrorBoundary.tsx";
import type { RouteOptions, RenderFn, RouteRecord, RouterOptions, NavigateOptions } from '../types/index.ts';

const routeMap = new Map<string, RouteRecord>();
let mountEl: HTMLElement | null = null;
let mode: 'hash' | 'history' = 'hash';
let onRouteChangeCallback: ((path: string) => void) | null = null;
let reactRoot: ReactDOM.Root | null = null;

function unmountReactRoot(): void {
  if (!reactRoot) return;
  try {
    reactRoot.unmount();
  } catch (e) {
    console.error('[Router] React unmount error:', e);
  }
  reactRoot = null;
}

function clearMount(): void {
  unmountReactRoot();
  if (mountEl) mountEl.innerHTML = '';
}

// ----------------- PATH NORMALIZATION -----------------
function normalize(path: string): string {
  if (!path) return '/';
  if (path === '*') return '*';
  const [pathname, query] = path.split('?');
  const cleanPath = pathname.replace(/\/+$/, '');
  const base = cleanPath.startsWith('/') ? cleanPath || '/' : '/' + cleanPath;
  return query ? `${base}?${query}` : base;
}

function routeKey(path: string): string {
  if (!path) return '/';
  if (path === '*') return '*';
  const normalized = normalize(path);
  return normalized.split('?')[0] || '/';
}

export function registerRoute(path: string, renderFn: RenderFn, opts: RouteOptions = {}): void {
  if (!path || typeof path !== 'string') {
    console.error('Invalid route path:', path);
    return;
  }
  routeMap.set(routeKey(path), { renderFn, opts });
}

export function navigateTo(path: string = '/', { replace = false }: NavigateOptions = {}): void {
  const p = normalize(path);
  if (mode === 'hash') {
    const hash = p === '/' ? '#/' : '#' + p;
    if (replace) location.replace(hash);
    else location.hash = hash;
    renderRoute();
  } else {
    if (replace) history.replaceState({}, '', p);
    else history.pushState({}, '', p);
    renderRoute();
  }
}

function getCurrentPath(): string {
  if (mode === 'hash') return location.hash.replace(/^#/, '') || '/';
  return `${location.pathname || '/'}${location.search || ''}`;
}

function findRoute(path: string): RouteRecord | null {
  const p = routeKey(path);
  if (routeMap.has(p)) return routeMap.get(p)!;
  if (routeMap.has('*')) return routeMap.get('*')!;
  return null;
}

function renderRoute(): void {
  if (!mountEl) return;
  const path = getCurrentPath();
  const matched = findRoute(path);

  if (!matched) {
    clearMount();
    mountEl.innerHTML = '<div>Page not found</div>';
    return;
  }

  const prev = mountEl.firstChild as (ChildNode & { cleanup?: () => void }) | null;
  if (prev && typeof prev.cleanup === 'function') {
    try { prev.cleanup(); }
    catch (e) { console.error('[Router] Cleanup error:', e); }
  }

  if (matched.opts?.auth && (!globalState.user || !globalState.token)) {
    if (path !== '/login') return navigateTo('/login', { replace: true });
  }

  if (typeof onRouteChangeCallback === 'function') onRouteChangeCallback(path);

  clearMount();

  if (matched.opts?.react) {
    reactRoot = ReactDOM.createRoot(mountEl);
    reactRoot.render(
      React.createElement(
        ErrorBoundary,
        null,
        React.createElement(matched.renderFn as React.ComponentType),
      ),
    );
    return;
  }

  const out = matched.renderFn();
  if (out instanceof HTMLElement || out instanceof DocumentFragment) mountEl.appendChild(out);
  else if (typeof out === 'string') mountEl.innerHTML = out;
  else console.error('Invalid renderFn output for route:', path, out);
}

export function startRouter(mountElement: HTMLElement | string, opts: RouterOptions = {}): void {
  mountEl = (typeof mountElement === 'string') ? document.querySelector(mountElement) : mountElement;
  mode = opts.mode === 'history' ? 'history' : 'hash';
  if (typeof opts.onRouteChange === 'function') onRouteChangeCallback = opts.onRouteChange;

  if (mode === 'hash') {
    window.addEventListener('hashchange', renderRoute);
    if (!location.hash) location.hash = '/';
  } else {
    window.addEventListener('popstate', renderRoute);
  }

  document.addEventListener('funzi:state', renderRoute);
  renderRoute();
}

export function getRouteByPath(path: string): RouteRecord | null {
  const p = routeKey(path);
  if (routeMap.has(p)) return routeMap.get(p)!;
  if (routeMap.has('*')) return routeMap.get('*')!;
  return null;
}
